// =============================================================================
// draftDates.js — which DATES hold unpublished work (Sep 2026)
// =============================================================================
// The console used to track one boolean: "there is an unpublished draft". Any
// successful publish cleared it, so solving October and November and then
// publishing November dropped the badge AND the close-tab warning while
// October's solve still existed only in this browser tab — one refresh and it
// was gone, silently.
//
// Now every draft mutation records the dates it touched, and a publish clears
// only the dates of the window it published. The badge and the warning stay up
// until nothing is left. Dates are local 'YYYY-MM-DD' strings, kept as a
// sorted, de-duplicated array so React state stays immutable and comparable.
// =============================================================================

import { formatDateRange } from '../utils/dateRange';

const ISO_PREFIX = /^\d{4}-\d{2}-\d{2}/;
// A solve window is a week, fortnight or month; anything longer is a bug in the
// caller, and an unbounded loop over it would hang the tab.
const MAX_WINDOW_DAYS = 400;

/** 'YYYY-MM-DD' built from LOCAL date parts (never toISOString, which is UTC). */
export function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local Date for an ISO day — `new Date('YYYY-MM-DD')` would parse as UTC. */
export function fromISODate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Every ISO day from start to end inclusive; [] for a missing or reversed range. */
export function datesBetween(startISO, endISO) {
  if (!startISO || !endISO || !ISO_PREFIX.test(startISO) || !ISO_PREFIX.test(endISO)) return [];
  const out = [];
  const cur = fromISODate(startISO.slice(0, 10));
  const end = fromISODate(endISO.slice(0, 10));
  while (cur <= end && out.length < MAX_WINDOW_DAYS) {
    out.push(toISODate(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

/** current ∪ dates, sorted. Returns `current` itself when nothing changes. */
export function addDraftDates(current, dates) {
  const valid = (dates || []).filter((d) => typeof d === 'string' && ISO_PREFIX.test(d));
  if (!valid.length) return current;
  const merged = new Set(current);
  valid.forEach((d) => merged.add(d.slice(0, 10)));
  if (merged.size === current.length) return current;
  return [...merged].sort();
}

/** current − dates. Returns `current` itself when nothing changes. */
export function removeDraftDates(current, dates) {
  const drop = new Set((dates || []).map((d) => String(d).slice(0, 10)));
  const kept = current.filter((d) => !drop.has(d));
  return kept.length === current.length ? current : kept;
}

/** Collapse sorted ISO days into contiguous runs: [{ start, end, days }]. */
export function toRanges(dates) {
  const ranges = [];
  [...new Set(dates)].sort().forEach((iso) => {
    const last = ranges[ranges.length - 1];
    if (last) {
      const next = fromISODate(last.end);
      next.setDate(next.getDate() + 1);
      if (toISODate(next) === iso) { last.end = iso; last.days += 1; return; }
    }
    ranges.push({ start: iso, end: iso, days: 1 });
  });
  return ranges;
}

/** "Mon 3 – Sun 9 Oct", or "Mon 3 Oct" for a single day. */
export function formatDraftRange({ start, end }) {
  const s = fromISODate(start);
  if (start === end) {
    // formatDateRange would print "Mon 3 – Mon 3 Oct"; take its end half.
    return formatDateRange(s, s).split(' – ')[1];
  }
  return formatDateRange(s, fromISODate(end));
}
